import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setAnswers } from '../../globals/application';
import RichEditor from '../RichEditor';

/* ===== Multiple Choice (single correct) ===== */
export const MultipleChoice = ({ showEquationButton = false }) => {
  const dispatch = useDispatch();
  const [mcqs, setMCQ] = useState([
    { answer: '', is_correct: false },
    { answer: '', is_correct: false },
    { answer: '', is_correct: false },
    { answer: '', is_correct: false },
  ]);

  const update = updated => {
    setMCQ(updated);
    dispatch(setAnswers(updated));
  };

  const setCorrect = index => {
    update(mcqs.map((a, i) => ({ ...a, is_correct: i === index })));
  };

  const setAnswer = (index, value) => {
    update(mcqs.map((a, i) => (i === index ? { ...a, answer: value } : a)));
  };

  const addRow = () => {
    update([...mcqs, { answer: '', is_correct: false }]);
  };

  const removeRow = index => {
    if (mcqs.length <= 2) return;
    const updated = mcqs
      .filter((_, i) => i !== index)
      .map((a, i) => ({
        ...a,
        is_correct: mcqs[index].is_correct && i === 0 ? true : a.is_correct,
      }));
    update(updated);
  };

  return (
    <div className="field-group">
      <label className="field-label">Answer Choices</label>
      <div className="answer-list">
        {mcqs.map((ans, index) => (
          <div key={index} className={`answer-row${ans.is_correct ? ' is-correct' : ''}`}>
            <span className="answer-number">{index + 1}</span>
            <div className="answer-editor-wrapper">
              <RichEditor
                value={ans.answer}
                onChange={val => setAnswer(index, val)}
                placeholder={`Answer ${index + 1}`}
                minHeight={40}
                showEquationButton={showEquationButton}
              />
            </div>
            <label className="answer-check">
              <input
                type="radio"
                name="mcq-correct"
                checked={ans.is_correct}
                onChange={() => setCorrect(index)}
              />
              Correct
            </label>
            {mcqs.length > 2 && (
              <button
                type="button"
                className="remove-answer-btn"
                onClick={() => removeRow(index)}
                title="Remove answer"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 10 }}
        onClick={addRow}
      >
        + Add Answer
      </button>
    </div>
  );
};

/* ===== True / False ===== */
export const TrueOrFalse = () => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);

  const choose = val => {
    setSelected(val);
    dispatch(setAnswers(val));
  };

  return (
    <div className="field-group">
      <label className="field-label">Correct Answer</label>
      <div className="tf-options">
        <label className={`tf-option${selected === 'true' ? ' selected' : ''}`}>
          <input type="radio" name="tf" value="true" onChange={() => choose('true')} />
          True
        </label>
        <label className={`tf-option${selected === 'false' ? ' selected' : ''}`}>
          <input type="radio" name="tf" value="false" onChange={() => choose('false')} />
          False
        </label>
      </div>
    </div>
  );
};

/* ===== Arrange In Order ===== */
export const ArrangeInOrder = ({ showEquationButton = false }) => {
  const dispatch = useDispatch();
  const [items, setItems] = useState([
    { ans: '', order: 1 },
    { ans: '', order: 2 },
    { ans: '', order: 3 },
    { ans: '', order: 4 },
  ]);

  const update = updated => {
    setItems(updated);
    dispatch(setAnswers(updated));
  };

  const setVal = (index, value) => {
    update(items.map((it, i) => (i === index ? { ...it, ans: value } : it)));
  };

  const addRow = () => {
    update([...items, { ans: '', order: items.length + 1 }]);
  };

  const removeRow = index => {
    if (items.length <= 2) return;
    const updated = items.filter((_, i) => i !== index).map((it, i) => ({ ...it, order: i + 1 }));
    update(updated);
  };

  return (
    <div className="field-group">
      <label className="field-label">Items in Correct Order</label>
      <div className="answer-list">
        {items.map((item, index) => (
          <div key={index} className="answer-row">
            <span className="answer-number">{index + 1}</span>
            <div className="answer-editor-wrapper">
              <RichEditor
                value={item.ans}
                onChange={val => setVal(index, val)}
                placeholder={`Item ${index + 1}`}
                minHeight={40}
                showEquationButton={showEquationButton}
              />
            </div>
            {items.length > 2 && (
              <button
                type="button"
                className="remove-answer-btn"
                onClick={() => removeRow(index)}
                title="Remove item"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 10 }}
        onClick={addRow}
      >
        + Add Item
      </button>
    </div>
  );
};

/* ===== Multi-Select ===== */
export const MultiSelect = ({ showEquationButton = false }) => {
  const dispatch = useDispatch();
  const [options, setOptions] = useState([
    { answer: '', is_correct: false },
    { answer: '', is_correct: false },
    { answer: '', is_correct: false },
    { answer: '', is_correct: false },
  ]);

  const update = updated => {
    setOptions(updated);
    dispatch(setAnswers(updated));
  };

  const setCorrect = (index, checked) => {
    update(options.map((a, i) => (i === index ? { ...a, is_correct: checked } : a)));
  };

  const setAnswer = (index, value) => {
    update(options.map((a, i) => (i === index ? { ...a, answer: value } : a)));
  };

  const addRow = () => {
    update([...options, { answer: '', is_correct: false }]);
  };

  const removeRow = index => {
    if (options.length <= 2) return;
    update(options.filter((_, i) => i !== index));
  };

  return (
    <div className="field-group">
      <label className="field-label">Answer Choices (select all correct)</label>
      <div className="answer-list">
        {options.map((opt, index) => (
          <div key={index} className={`answer-row${opt.is_correct ? ' is-correct' : ''}`}>
            <span className="answer-number">{index + 1}</span>
            <div className="answer-editor-wrapper">
              <RichEditor
                value={opt.answer}
                onChange={val => setAnswer(index, val)}
                placeholder={`Answer ${index + 1}`}
                minHeight={40}
                showEquationButton={showEquationButton}
              />
            </div>
            <label className="answer-check">
              <input
                type="checkbox"
                checked={opt.is_correct}
                onChange={e => setCorrect(index, e.target.checked)}
              />
              Correct
            </label>
            {options.length > 2 && (
              <button
                type="button"
                className="remove-answer-btn"
                onClick={() => removeRow(index)}
                title="Remove answer"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 10 }}
        onClick={addRow}
      >
        + Add Answer
      </button>
    </div>
  );
};

/* ===== Swipe Left/Right ===== */
export const SwipeLeftRight = () => {
  const dispatch = useDispatch();
  const [config, setConfig] = useState({
    correctAnswer: null, // 'left' or 'right'
    leftLabel: 'False',
    rightLabel: 'True',
  });

  const update = updated => {
    setConfig(updated);
    dispatch(setAnswers(updated));
  };

  const selectAnswer = direction => {
    update({ ...config, correctAnswer: direction });
  };

  const updateLabel = (side, value) => {
    update({ ...config, [side === 'left' ? 'leftLabel' : 'rightLabel']: value });
  };

  return (
    <div className="field-group">
      <label className="field-label">Swipe Labels</label>
      <div className="swipe-labels-row">
        <div className="swipe-label-input">
          <label>Left (Swipe Left = )</label>
          <input
            type="text"
            className="field-input"
            value={config.leftLabel}
            onChange={e => updateLabel('left', e.target.value)}
            placeholder="e.g., False, Incorrect, No"
          />
        </div>
        <div className="swipe-label-input">
          <label>Right (Swipe Right = )</label>
          <input
            type="text"
            className="field-input"
            value={config.rightLabel}
            onChange={e => updateLabel('right', e.target.value)}
            placeholder="e.g., True, Correct, Yes"
          />
        </div>
      </div>

      <label className="field-label" style={{ marginTop: 16 }}>
        Correct Answer
      </label>
      <div className="swipe-options">
        <button
          type="button"
          className={`swipe-option left${config.correctAnswer === 'left' ? ' selected' : ''}`}
          onClick={() => selectAnswer('left')}
        >
          <span className="swipe-arrow">←</span>
          <span>{config.leftLabel || 'Left'}</span>
        </button>
        <button
          type="button"
          className={`swipe-option right${config.correctAnswer === 'right' ? ' selected' : ''}`}
          onClick={() => selectAnswer('right')}
        >
          <span>{config.rightLabel || 'Right'}</span>
          <span className="swipe-arrow">→</span>
        </button>
      </div>
    </div>
  );
};

/* ===== Drag and Drop (Matching/Categorization) ===== */
export const DragAndDrop = ({ showEquationButton = false }) => {
  const dispatch = useDispatch();
  const [zones, setZones] = useState([
    { id: 1, label: 'Category A' },
    { id: 2, label: 'Category B' },
  ]);
  const [categoryItems, setCategoryItems] = useState([
    { text: '', zoneId: 1 },
    { text: '', zoneId: 1 },
    { text: '', zoneId: 2 },
    { text: '', zoneId: 2 },
  ]);

  const updateCategorize = (newZones, newItems) => {
    setZones(newZones);
    setCategoryItems(newItems);
    dispatch(setAnswers({ mode: 'categorize', zones: newZones, items: newItems }));
  };

  // Initialize on mount
  React.useEffect(() => {
    dispatch(setAnswers({ mode: 'categorize', zones, items: categoryItems }));
  }, [dispatch]);

  // Category/Zone handlers
  const setZoneLabel = (index, value) => {
    const newZones = zones.map((z, i) => (i === index ? { ...z, label: value } : z));
    updateCategorize(newZones, categoryItems);
  };

  const addZone = () => {
    const newZone = {
      id: zones.length + 1,
      label: `Category ${String.fromCharCode(65 + zones.length)}`,
    };
    updateCategorize([...zones, newZone], categoryItems);
  };

  const removeZone = index => {
    if (zones.length <= 2) return;
    const removedZoneId = zones[index].id;
    const newZones = zones.filter((_, i) => i !== index).map((z, i) => ({ ...z, id: i + 1 }));
    // Reassign items from removed zone to first zone
    const newItems = categoryItems.map(item =>
      item.zoneId === removedZoneId ? { ...item, zoneId: newZones[0].id } : item
    );
    updateCategorize(newZones, newItems);
  };

  const setCategoryItemText = (index, value) => {
    const newItems = categoryItems.map((it, i) => (i === index ? { ...it, text: value } : it));
    updateCategorize(zones, newItems);
  };

  const setCategoryItemZone = (index, zoneId) => {
    const newItems = categoryItems.map((it, i) =>
      i === index ? { ...it, zoneId: parseInt(zoneId) } : it
    );
    updateCategorize(zones, newItems);
  };

  const addCategoryItem = () => {
    updateCategorize(zones, [...categoryItems, { text: '', zoneId: zones[0].id }]);
  };

  const removeCategoryItem = index => {
    if (categoryItems.length <= 2) return;
    updateCategorize(
      zones,
      categoryItems.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="field-group">
      <label className="field-label">Categories (Match Targets)</label>
      <p className="field-hint">
        Define the categories on the left side. Students will drag items to match with these categories.
      </p>
      <div className="zones-list">
        {zones.map((zone, index) => (
          <div key={zone.id} className="zone-row">
            <input
              type="text"
              className="field-input"
              value={zone.label}
              onChange={e => setZoneLabel(index, e.target.value)}
              placeholder={`Category ${index + 1}`}
            />
            {zones.length > 2 && (
              <button
                type="button"
                className="remove-answer-btn"
                onClick={() => removeZone(index)}
                title="Remove category"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 10 }}
        onClick={addZone}
      >
        + Add Category
      </button>

      <label className="field-label" style={{ marginTop: 20 }}>
        Items to Match
      </label>
      <p className="field-hint">Enter items and select which category each item matches with.</p>
      <div className="answer-list">
        {categoryItems.map((item, index) => (
          <div key={index} className="answer-row category-item-row">
            <div className="answer-editor-wrapper">
              <RichEditor
                value={item.text}
                onChange={val => setCategoryItemText(index, val)}
                placeholder={`Item ${index + 1}`}
                minHeight={36}
                showEquationButton={showEquationButton}
              />
            </div>
            <select
              className="field-select zone-select"
              value={item.zoneId}
              onChange={e => setCategoryItemZone(index, e.target.value)}
            >
              {zones.map(z => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
            {categoryItems.length > 2 && (
              <button
                type="button"
                className="remove-answer-btn"
                onClick={() => removeCategoryItem(index)}
                title="Remove item"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 10 }}
        onClick={addCategoryItem}
      >
        + Add Item
      </button>
    </div>
  );
};

/* ===== Matching Pairs (Terms ↔ Definitions) ===== */
export const MatchingPairs = ({ showEquationButton = false }) => {
  const dispatch = useDispatch();
  const [pairs, setPairs] = useState([
    { term: '', definition: '', order: 1 },
    { term: '', definition: '', order: 2 },
    { term: '', definition: '', order: 3 },
  ]);

  // Use useEffect to dispatch to Redux after state updates (avoids render-time dispatch warning)
  React.useEffect(() => {
    dispatch(setAnswers(pairs));
  }, [pairs, dispatch]);

  // Use functional update to avoid stale closure issues
  const setTerm = (index, value) => {
    setPairs(currentPairs => 
      currentPairs.map((p, i) => (i === index ? { ...p, term: value } : p))
    );
  };

  const setDefinition = (index, value) => {
    setPairs(currentPairs => 
      currentPairs.map((p, i) => (i === index ? { ...p, definition: value } : p))
    );
  };

  const addPair = () => {
    setPairs(currentPairs => 
      [...currentPairs, { term: '', definition: '', order: currentPairs.length + 1 }]
    );
  };

  const removePair = index => {
    setPairs(currentPairs => {
      if (currentPairs.length <= 2) return currentPairs;
      return currentPairs.filter((_, i) => i !== index).map((p, i) => ({ ...p, order: i + 1 }));
    });
  };

  return (
    <div className="field-group">
      <label className="field-label">Matching Pairs</label>
      <p className="field-hint">
        Enter terms on the left and their matching definitions on the right. 
        Students will tap to connect matching pairs.
      </p>
      <div className="matching-pairs-list">
        {pairs.map((pair, index) => (
          <div key={index} className="matching-pair-row">
            <span className="pair-number">{index + 1}</span>
            <div className="pair-inputs">
              <div className="pair-input-group">
                <label className="pair-label">Term</label>
                <RichEditor
                  value={pair.term}
                  onChange={val => setTerm(index, val)}
                  placeholder={`Term ${index + 1}`}
                  minHeight={40}
                  showEquationButton={showEquationButton}
                />
              </div>
              <div className="pair-arrow">↔</div>
              <div className="pair-input-group">
                <label className="pair-label">Definition</label>
                <RichEditor
                  value={pair.definition}
                  onChange={val => setDefinition(index, val)}
                  placeholder={`Definition ${index + 1}`}
                  minHeight={40}
                  showEquationButton={showEquationButton}
                />
              </div>
            </div>
            {pairs.length > 2 && (
              <button
                type="button"
                className="remove-answer-btn"
                onClick={() => removePair(index)}
                title="Remove pair"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 10 }}
        onClick={addPair}
      >
        + Add Pair
      </button>
    </div>
  );
};
